import { useQuery } from '@tanstack/react-query';

import { api } from './api';

export interface CustomerRow {
  id: string;
  name: string;
  number: string | null;
  country: string | null;
  currency: string;
  visible: boolean;
  /** One of the three levels that decide a new entry's billable flag. */
  billable: boolean;
  _count: { projects: number };
  comment: string | null;
  color: string | null;
  /**
   * Budgets. Absent entirely for anyone who may not see money - the API strips
   * them rather than sending zeros, so `undefined` means "not allowed to know"
   * and `'0'` means "no budget set".
   */
  budgetMinor?: string;
  timeBudgetSeconds?: number;
  budgetType?: string | null;
  /** Empty means visible to everybody. */
  teams: { id: string; name: string }[];
}

export interface ProjectRow {
  id: string;
  name: string;
  number: string | null;
  visible: boolean;
  /** Whether hours booked here are sold to a client. Drives utilisation. */
  billable: boolean;
  /** A note somebody wrote about the project. 9 carry one. */
  comment: string | null;
  /** Hex, or nothing. 6 carry one. */
  color: string | null;
  start: string | null;
  end: string | null;
  /** Whether activities that belong to no project may be booked here. */
  globalActivities: boolean;
  /**
   * Money already billed against the project outside the timesheet, from the
   * previous system's partial-billing table. Absent - not zero - for anyone who
   * may not see money, the same as the budget fields.
   */
  billedMinor?: string;
  billings?: Array<{ id: string; amountMinor: string; billedOn: string; comment: string | null }>;
  customer: { id: string; name: string };
  _count: { activities: number };
  /**
   * Budgets. Absent entirely for anyone who may not see money - the API strips
   * them rather than sending zeros, so `undefined` means "not allowed to know"
   * and `'0'` means "no budget set".
   */
  budgetMinor?: string;
  timeBudgetSeconds?: number;
  budgetType?: string | null;
  /** Empty means visible to everybody. */
  teams: { id: string; name: string }[];
}

export interface ActivityRow {
  id: string;
  name: string;
  /** Generated from `activityNumberFormat` on create. All 117 migrated ones have one. */
  number: string | null;
  visible: boolean;
  /** One of the three levels that decide a new entry's billable flag. */
  billable: boolean;
  project: { id: string; name: string } | null;
  comment: string | null;
  color: string | null;
  /**
   * Budgets. Absent entirely for anyone who may not see money - the API strips
   * them rather than sending zeros, so `undefined` means "not allowed to know"
   * and `'0'` means "no budget set".
   */
  budgetMinor?: string;
  timeBudgetSeconds?: number;
  budgetType?: string | null;
  /** Empty means visible to everybody. */
  teams: { id: string; name: string }[];
}

/**
 * Retired rows are excluded by default, and asked for explicitly.
 *
 * The booking pickers must never offer something retired - that is what retiring
 * it means. The administration screens must show it, or hiding something makes it
 * vanish with no way to bring it back, which is exactly what happened: a
 * mis-clicked eye icon removed a customer from the only list that could have
 * un-clicked it.
 *
 * The flag is part of the query key, so the two callers do not share one cached
 * answer and a picker cannot end up rendering the administrator's list.
 */
const catalogQuery = <T,>(kind: string, includeHidden: boolean) => ({
  queryKey: ['catalog', kind, { includeHidden }] as const,
  queryFn: () =>
    api.get<T>(`/catalog/${kind}${includeHidden ? '?includeHidden=true' : ''}`),
});

export const useCustomers = (includeHidden = false) =>
  useQuery(catalogQuery<CustomerRow[]>('customers', includeHidden));

export const useProjects = (includeHidden = false) =>
  useQuery(catalogQuery<ProjectRow[]>('projects', includeHidden));

/**
 * Whether an activity may go on a project: its own project's, or a shared one
 * where the project accepts them. The same rule bookingRefusal() applies on save.
 * The pickers used to offer shared activities on the projects that forbid them,
 * and the save then refused them.
 */
export function fitsProject(
  activity: { project: { id: string } | null },
  projectId: string | null,
  projects: Pick<ProjectRow, 'id' | 'globalActivities'>[] | undefined,
): boolean {
  if (activity.project) return activity.project.id === projectId;
  return projects?.find((p) => p.id === projectId)?.globalActivities ?? true;
}

export const useActivities = (includeHidden = false) =>
  useQuery(catalogQuery<ActivityRow[]>('activities', includeHidden));

export interface TagRow {
  id: string;
  name: string;
  color: string | null;
  _count: { entries: number };
}

export const useTags = () =>
  useQuery({ queryKey: ['catalog', 'tags'], queryFn: () => api.get<TagRow[]>('/catalog/tags') });
